/**
 * CU-14zefhy2ydj U8 + C4: copy an answer as formatted text (headings, lists, tables and links survive a paste into Word, Excel
 * or an email) and take a table of an answer away as CSV (opens in Excel). Works on the blocks `parseMarkdown` makes, so what
 * is copied is what the chat shows, and nothing is ever built from raw HTML of the reply: every text is escaped.
 * Pure helpers (no Angular); the two browser calls take their `document` / clipboard as arguments, so the spec runs under node.
 */

import {MdBlock, MdInline, parseMarkdown, Linkify} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-markdown';

type TableBlock = Extract<MdBlock, {kind: 'table'}>;

const textOf = (runs: MdInline[]): string => runs.map((r) => r.text).join('');

const escapeHtml = (text: string): string =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** The link of an inline run as an absolute URL on `origin` (query kept), or '' when the run is not a link. */
function hrefOf(run: MdInline, origin: string): string {
    return run.link ? `${origin || ''}${run.link}` : '';
}

function inlineHtml(runs: MdInline[], origin: string): string {
    return runs
        .map((run) => {
            let html = escapeHtml(run.text).replace(/\n/g, '<br>');
            if (run.code) {
                html = `<code>${html}</code>`;
            }
            if (run.bold) {
                html = `<strong>${html}</strong>`;
            }
            if (run.italic) {
                html = `<em>${html}</em>`;
            }
            const href = hrefOf(run, origin);
            return href ? `<a href="${escapeHtml(href)}">${html}</a>` : html;
        })
        .join('');
}

/** The answer as HTML for the clipboard (`text/html`). */
export function blocksToHtml(blocks: MdBlock[], origin = ''): string {
    const out: string[] = [];
    for (const b of blocks) {
        switch (b.kind) {
            case 'p':
                out.push(`<p>${inlineHtml(b.inlines, origin)}</p>`);
                break;
            case 'heading': {
                const level = Math.min(Math.max(b.level, 1), 6);
                out.push(`<h${level}>${inlineHtml(b.inlines, origin)}</h${level}>`);
                break;
            }
            case 'list': {
                const tag = b.ordered ? 'ol' : 'ul';
                const start = b.ordered && b.start !== 1 ? ` start="${b.start}"` : '';
                // nested items are indented with spaces: a paste keeps the shape without nested markup
                const items = b.items.map((it) => `<li>${'&nbsp;'.repeat(it.level * 4)}${inlineHtml(it.inlines, origin)}</li>`);
                out.push(`<${tag}${start}>${items.join('')}</${tag}>`);
                break;
            }
            case 'table': {
                const head = b.header.map((h) => `<th>${inlineHtml(h, origin)}</th>`).join('');
                const rows = b.rows.map((r) => `<tr>${r.map((c) => `<td>${inlineHtml(c, origin)}</td>`).join('')}</tr>`).join('');
                out.push(`<table border="1" cellspacing="0" cellpadding="4"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`);
                break;
            }
            case 'code':
                out.push(`<pre>${escapeHtml(b.text)}</pre>`);
                break;
            case 'hr':
                out.push('<hr>');
                break;
        }
    }
    return out.join('');
}

/** The answer as plain text for the clipboard (`text/plain`): no markdown marks; tables as tab-separated rows (paste into Excel). */
export function blocksToPlain(blocks: MdBlock[]): string {
    const out: string[] = [];
    for (const b of blocks) {
        switch (b.kind) {
            case 'p':
            case 'heading':
                out.push(textOf(b.inlines));
                break;
            case 'list':
                out.push(b.items.map((it, i) => `${'  '.repeat(it.level)}${b.ordered ? `${b.start + i}.` : '-'} ${textOf(it.inlines)}`).join('\n'));
                break;
            case 'table':
                out.push([b.header, ...b.rows].map((row) => row.map((cell) => textOf(cell).replace(/[\t\r\n]+/g, ' ')).join('\t')).join('\n'));
                break;
            case 'code':
                out.push(b.text);
                break;
            case 'hr':
                out.push('---');
                break;
        }
    }
    return out.join('\n\n');
}

export interface CopyPayload {
    plain: string;
    html: string;
}

/** What "Copy" puts on the clipboard for a reply's markdown text. */
export function copyPayload(markdown: string, linkify?: Linkify, origin = ''): CopyPayload {
    const blocks = parseMarkdown(markdown || '', linkify);
    return {plain: blocks.length ? blocksToPlain(blocks) : markdown || '', html: blocksToHtml(blocks, origin)};
}

/**
 * Copies formatted text when the browser allows it (`ClipboardItem`), else plain text. Returns whether anything was copied;
 * the caller may then try its own fallback.
 */
export async function copyFormatted(
    payload: CopyPayload,
    clipboard: {write?: (items: any[]) => Promise<void>; writeText?: (text: string) => Promise<void>} | null | undefined,
    clipboardItem: any
): Promise<boolean> {
    if (!clipboard) {
        return false;
    }
    if (clipboardItem && typeof clipboard.write === 'function' && payload.html) {
        try {
            const Item = clipboardItem;
            await clipboard.write([
                new Item({
                    'text/html': new Blob([payload.html], {type: 'text/html'}),
                    'text/plain': new Blob([payload.plain], {type: 'text/plain'})
                })
            ]);
            return true;
        } catch (e) {
            // fall through to plain text
        }
    }
    if (typeof clipboard.writeText === 'function') {
        try {
            await clipboard.writeText(payload.plain);
            return true;
        } catch (e) {
            return false;
        }
    }
    return false;
}

// ----- tables as CSV (C4) --------------------------------------------------------------------------

/** The tables of a reply, in order. */
export function tablesOf(markdown: string, linkify?: Linkify): TableBlock[] {
    return parseMarkdown(markdown || '', linkify).filter((b): b is TableBlock => b.kind === 'table');
}

/**
 * One CSV cell. A cell a spreadsheet would run as a formula (= + - @ followed by more than a number) gets a leading apostrophe, so
 * a reply can never plant a formula in the user's file.
 */
export function csvCell(value: string): string {
    let text = (value || '').replace(/\r?\n/g, ' ');
    if (/^[=@\t\r]/.test(text) || (/^[+-]/.test(text) && !/^[+-](\d[\d.,\s]*%?)?$/.test(text))) {
        text = `'${text}`;
    }
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** A table as CSV text (CRLF rows, header first). */
export function tableToCsv(table: TableBlock): string {
    const row = (cells: MdInline[][]) => cells.map((c) => csvCell(textOf(c))).join(',');
    return [row(table.header), ...table.rows.map(row)].join('\r\n') + '\r\n';
}

/** A safe file name for a downloaded table: `assistant-table-<n>-<yyyymmdd>.csv`. */
export function csvFileName(index: number, now: Date = new Date()): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `assistant-table-${Math.max(1, index)}-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}.csv`;
}

/**
 * Saves CSV text as a file through a temporary link (`Blob`). The BOM makes Excel read it as UTF-8. Returns false when the
 * browser refuses (no `document`, blocked download).
 */
export function downloadCsv(doc: any, fileName: string, csv: string): boolean {
    try {
        if (!doc || typeof Blob === 'undefined' || !doc.createElement) {
            return false;
        }
        const url = URL.createObjectURL(new Blob(['\uFEFF', csv], {type: 'text/csv;charset=utf-8'}));
        const link = doc.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        doc.body.appendChild(link);
        link.click();
        doc.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        return true;
    } catch (e) {
        return false;
    }
}
